
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { debugManager } from '@/utils/debugging/DebugManager';

/**
 * RouteTransitionTimings - Measures and logs route transition performance
 * Ultra-optimized to minimize overhead and run only when needed
 */
const RouteTransitionTimings: React.FC = () => {
  const location = useLocation();
  const prevLocationRef = useRef<string | null>(null);
  const transitionStartTimeRef = useRef<number>(0);
  const enabledRef = useRef(process.env.NODE_ENV !== 'production');
  
  useEffect(() => {
    // Skip in production to reduce overhead
    if (!enabledRef.current) return;
    
    // Skip if tab is not visible
    if (document.visibilityState !== 'visible') return;
    
    // Apply aggressive sampling to reduce overhead (only track 2.5% of transitions - reduced from 5%)
    if (Math.random() > 0.025) return;
    
    const currentPath = location.pathname + location.search;
    const now = performance.now();
    
    // If we have a previous location, log the transition time
    if (prevLocationRef.current) {
      const transitionDuration = now - transitionStartTimeRef.current;
      
      // Only log transitions over 1000ms to reduce noise (increased from 750ms)
      if (transitionDuration > 1000) {
        debugManager.debug('RouteTransition', `Transition: ${prevLocationRef.current} → ${currentPath}`, {
          duration: `${transitionDuration.toFixed(0)}ms`
        });
      
        // Warn if transition is very slow (over 2000ms - increased from 1500ms)
        if (transitionDuration > 2000) {
          debugManager.warn('RouteTransition', `Slow transition: ${prevLocationRef.current} → ${currentPath}`, {
            duration: `${transitionDuration.toFixed(0)}ms`
          });
        }
      }
    }
    
    // Set current location as previous for next transition
    prevLocationRef.current = currentPath;
    transitionStartTimeRef.current = now;
  }, [location]);
  
  // This component doesn't render anything
  return null;
};

export default React.memo(RouteTransitionTimings);
